import React, { useState, useEffect } from 'react';
import { 
  MapPin, Globe, Building2, Map, Plus, Edit2, Trash2, X, 
  Search, CheckCircle2, AlertCircle, RefreshCw, ChevronRight, Layers
} from 'lucide-react';
import { AdminService } from '../../services/api';

export default function LocationMaster({ showCustomAlert }) {
  // Tabs: 'countries' | 'regions' | 'cities'
  const [activeTab, setActiveTab] = useState('countries');

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Selected filters for cascading view
  const [filterCountryId, setFilterCountryId] = useState('ALL');
  const [filterRegionId, setFilterRegionId] = useState('ALL');

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('country'); // 'country' | 'region' | 'city'
  const [saving, setSaving] = useState(false);

  // Forms
  const [countryForm, setCountryForm] = useState({ id: null, country_code: '', country_name: '', phone_code: '', currency_code: 'USD', is_active: 1 });
  const [regionForm, setRegionForm] = useState({ id: null, country_id: '', region_name: '', region_type: 'state', is_active: 1 });
  const [cityForm, setCityForm] = useState({ id: null, region_id: '', city_name: '', postal_code: '', is_active: 1 });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'regions') {
      fetchRegions();
    } else if (activeTab === 'cities') {
      fetchCities();
    }
  }, [activeTab, filterCountryId, filterRegionId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [cRes, rRes, ciRes] = await Promise.allSettled([
        AdminService.getCountries(),
        AdminService.getRegions(),
        AdminService.getCities()
      ]);
      if (cRes.status === 'fulfilled' && cRes.value?.status === 1) setCountries(cRes.value.data || []);
      if (rRes.status === 'fulfilled' && rRes.value?.status === 1) setRegions(rRes.value.data || []);
      if (ciRes.status === 'fulfilled' && ciRes.value?.status === 1) setCities(ciRes.value.data || []);
    } catch (err) {
      console.error(err);
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await AdminService.getCountries();
      if (res.status === 1) setCountries(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRegions = async () => {
    try {
      const countryParam = filterCountryId !== 'ALL' ? filterCountryId : null;
      const res = await AdminService.getRegions(countryParam);
      if (res.status === 1) setRegions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCities = async () => {
    try {
      const regionParam = filterRegionId !== 'ALL' ? filterRegionId : null;
      const res = await AdminService.getCities(regionParam);
      if (res.status === 1) setCities(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Open Add Modals
  const handleOpenAddCountry = () => {
    setCountryForm({ id: null, country_code: '', country_name: '', phone_code: '', currency_code: 'USD', is_active: 1 });
    setModalType('country');
    setShowModal(true);
  };

  const handleOpenAddRegion = () => {
    const defaultCountry = filterCountryId !== 'ALL' ? filterCountryId : (countries[0]?.id || '');
    setRegionForm({ id: null, country_id: defaultCountry, region_name: '', region_type: 'state', is_active: 1 });
    setModalType('region');
    setShowModal(true);
  };

  const handleOpenAddCity = () => {
    const defaultRegion = filterRegionId !== 'ALL' ? filterRegionId : (regions[0]?.id || '');
    setCityForm({ id: null, region_id: defaultRegion, city_name: '', postal_code: '', is_active: 1 });
    setModalType('city');
    setShowModal(true);
  };

  // Open Edit Modals
  const handleOpenEditCountry = (c) => {
    setCountryForm({ id: c.id, country_code: c.country_code, country_name: c.country_name, phone_code: c.phone_code || '', currency_code: c.currency_code || 'USD', is_active: c.is_active });
    setModalType('country');
    setShowModal(true);
  };

  const handleOpenEditRegion = (r) => {
    setRegionForm({ id: r.id, country_id: r.country_id, region_name: r.region_name, region_type: r.region_type || 'state', is_active: r.is_active });
    setModalType('region');
    setShowModal(true);
  };

  const handleOpenEditCity = (ci) => {
    setCityForm({ id: ci.id, region_id: ci.region_id, city_name: ci.city_name, postal_code: ci.postal_code || '', is_active: ci.is_active });
    setModalType('city');
    setShowModal(true);
  };

  // Save Handlers
  const handleSaveCountry = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await AdminService.saveCountry(countryForm);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('Country saved successfully');
        setShowModal(false);
        fetchCountries();
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRegion = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await AdminService.saveRegion(regionForm);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('State / Province saved successfully');
        setShowModal(false);
        fetchRegions();
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await AdminService.saveCity(cityForm);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('City / Area saved successfully');
        setShowModal(false);
        fetchCities();
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete Handlers
  const handleDeleteCountry = async (id, name) => {
    if (!window.confirm(`Delete country "${name}" and all its regions?`)) return;
    try {
      const res = await AdminService.deleteCountry(id);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('Country deleted');
        fetchInitialData();
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    }
  };

  const handleDeleteRegion = async (id, name) => {
    if (!window.confirm(`Delete region "${name}" and all its cities?`)) return;
    try {
      const res = await AdminService.deleteRegion(id);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('Region deleted');
        fetchRegions();
        fetchCities();
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    }
  };

  const handleDeleteCity = async (id, name) => {
    if (!window.confirm(`Delete city/area "${name}"?`)) return;
    try {
      const res = await AdminService.deleteCity(id);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('City/Area deleted');
        fetchCities();
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    }
  };

  // Filtered lists for table search
  const filteredCountries = countries.filter(c => {
    const q = searchTerm.toLowerCase();
    return (c.country_name && c.country_name.toLowerCase().includes(q)) || (c.country_code && c.country_code.toLowerCase().includes(q));
  });

  const filteredRegions = regions.filter(r => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = (r.region_name && r.region_name.toLowerCase().includes(q)) || (r.country_name && r.country_name.toLowerCase().includes(q));
    const matchesCountry = filterCountryId === 'ALL' || String(r.country_id) === String(filterCountryId);
    return matchesSearch && matchesCountry;
  });

  const filteredCities = cities.filter(ci => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = (ci.city_name && ci.city_name.toLowerCase().includes(q)) || (ci.region_name && ci.region_name.toLowerCase().includes(q)) || (ci.country_name && ci.country_name.toLowerCase().includes(q));
    const matchesRegion = filterRegionId === 'ALL' || String(ci.region_id) === String(filterRegionId);
    return matchesSearch && matchesRegion;
  });

  return (
    <div className="leads-management-wrapper" style={{ padding: '1.5rem', maxWidth: '1280px', margin: '0 auto' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <MapPin size={24} color="#0284c7" />
            Global Location Master
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
            Manage hierarchical geographic coverage (Countries, States / Emirates / Governorates, and Cities / Operational Areas).
          </p>
        </div>

        {/* ADD ACTION BUTTON DEPENDING ON ACTIVE TAB */}
        {activeTab === 'countries' && (
          <button onClick={handleOpenAddCountry} style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '0.6rem 1.1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 4px rgba(2,132,199,0.2)' }}>
            <Plus size={16} /> Add Country
          </button>
        )}
        {activeTab === 'regions' && (
          <button onClick={handleOpenAddRegion} style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '0.6rem 1.1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 4px rgba(2,132,199,0.2)' }}>
            <Plus size={16} /> Add State / Emirate / Province
          </button>
        )}
        {activeTab === 'cities' && (
          <button onClick={handleOpenAddCity} style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '0.6rem 1.1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 4px rgba(2,132,199,0.2)' }}>
            <Plus size={16} /> Add City / Service Area
          </button>
        )}
      </div>

      {/* 3-LEVEL TABS */}
      <div className="leads-subtabs-nav" style={{ marginBottom: '1.25rem' }}>
        <button 
          className={`leads-tab-btn ${activeTab === 'countries' ? 'active' : ''}`}
          onClick={() => { setActiveTab('countries'); setSearchTerm(''); }}
        >
          <Globe size={16} /> Level 1: Countries ({countries.length})
        </button>
        <button 
          className={`leads-tab-btn ${activeTab === 'regions' ? 'active' : ''}`}
          onClick={() => { setActiveTab('regions'); setSearchTerm(''); }}
        >
          <Map size={16} /> Level 2: States / Emirates / Provinces ({regions.length})
        </button>
        <button 
          className={`leads-tab-btn ${activeTab === 'cities' ? 'active' : ''}`}
          onClick={() => { setActiveTab('cities'); setSearchTerm(''); }}
        >
          <Building2 size={16} /> Level 3: Cities & Operational Areas ({cities.length})
        </button>
      </div>

      {/* FILTER & SEARCH TOOLBAR */}
      <div className="leads-controls-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: '1 1 360px' }}>
          <div className="leads-search-box" style={{ flex: 1, minWidth: '220px' }}>
            <Search size={16} className="search-icon" />
            <input 
              type="text" 
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* DYNAMIC REGIONAL FILTER DROPDOWNS */}
          {activeTab === 'regions' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.4rem 0.65rem' }}>
              <Globe size={15} color="#0284c7" />
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Country:</span>
              <select
                value={filterCountryId}
                onChange={(e) => setFilterCountryId(e.target.value)}
                style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', outline: 'none' }}
              >
                <option value="ALL">All Countries</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.country_name} ({c.country_code})</option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'cities' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.4rem 0.65rem' }}>
              <Map size={15} color="#0284c7" />
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Region:</span>
              <select
                value={filterRegionId}
                onChange={(e) => setFilterRegionId(e.target.value)}
                style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', outline: 'none' }}
              >
                <option value="ALL">All States / Emirates</option>
                {regions.map(r => (
                  <option key={r.id} value={r.id}>{r.region_name} ({r.country_name})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <button className="leads-refresh-btn" onClick={fetchInitialData} title="Refresh">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* TABLE CONTENT */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        
        {/* 1. COUNTRIES TABLE */}
        {activeTab === 'countries' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                <th style={{ padding: '0.75rem 1rem' }}>ISO Code</th>
                <th style={{ padding: '0.75rem 1rem' }}>Country Name</th>
                <th style={{ padding: '0.75rem 1rem' }}>Dialing Code</th>
                <th style={{ padding: '0.75rem 1rem' }}>Default Currency</th>
                <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCountries.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No countries found.</td></tr>
              ) : (
                filteredCountries.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.75rem 1rem' }}><code style={{ background: '#eff6ff', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>{c.country_code}</code></td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#0f172a' }}>{c.country_name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#475569' }}>{c.phone_code || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#475569' }}>{c.currency_code || 'USD'}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ color: c.is_active ? '#059669' : '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>
                        ● {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleOpenEditCountry(c)} style={{ background: 'none', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Edit2 size={12} /> Edit
                        </button>
                        <button onClick={() => handleDeleteCountry(c.id, c.country_name)} style={{ background: 'none', border: '1px solid #fca5a5', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* 2. REGIONS TABLE */}
        {activeTab === 'regions' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                <th style={{ padding: '0.75rem 1rem' }}>Region / State / Emirate</th>
                <th style={{ padding: '0.75rem 1rem' }}>Classification Type</th>
                <th style={{ padding: '0.75rem 1rem' }}>Country</th>
                <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegions.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No states/regions found.</td></tr>
              ) : (
                filteredRegions.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#0f172a' }}>{r.region_name}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ textTransform: 'capitalize', background: '#f1f5f9', padding: '2px 8px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                        {r.region_type}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#334155' }}>{r.country_name} ({r.country_code})</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ color: r.is_active ? '#059669' : '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>
                        ● {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleOpenEditRegion(r)} style={{ background: 'none', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Edit2 size={12} /> Edit
                        </button>
                        <button onClick={() => handleDeleteRegion(r.id, r.region_name)} style={{ background: 'none', border: '1px solid #fca5a5', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* 3. CITIES TABLE */}
        {activeTab === 'cities' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                <th style={{ padding: '0.75rem 1rem' }}>City / Service Area</th>
                <th style={{ padding: '0.75rem 1rem' }}>State / Emirate</th>
                <th style={{ padding: '0.75rem 1rem' }}>Country</th>
                <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCities.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No cities/areas found.</td></tr>
              ) : (
                filteredCities.map(ci => (
                  <tr key={ci.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#0f172a' }}>{ci.city_name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#334155' }}>{ci.region_name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#64748b' }}>{ci.country_name}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ color: ci.is_active ? '#059669' : '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>
                        ● {ci.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleOpenEditCity(ci)} style={{ background: 'none', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Edit2 size={12} /> Edit
                        </button>
                        <button onClick={() => handleDeleteCity(ci.id, ci.city_name)} style={{ background: 'none', border: '1px solid #fca5a5', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

      </div>

      {/* DYNAMIC MODALS FOR LOCATION TIERS */}
      {showModal && (
        <div className="lead-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="lead-modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>
                <MapPin size={18} color="#0284c7" />
                {modalType === 'country' && (countryForm.id ? 'Edit Country' : 'Add New Country')}
                {modalType === 'region' && (regionForm.id ? 'Edit State / Emirate' : 'Add State / Emirate / Province')}
                {modalType === 'city' && (cityForm.id ? 'Edit City / Area' : 'Add City / Service Area')}
              </h3>
              <button className="close-btn" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>

            {/* COUNTRY FORM */}
            {modalType === 'country' && (
              <form onSubmit={handleSaveCountry}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>ISO Code *</label>
                      <input type="text" required placeholder="e.g. AE" maxLength={10} value={countryForm.country_code} onChange={e => setCountryForm({ ...countryForm, country_code: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Country Name *</label>
                      <input type="text" required placeholder="e.g. United Arab Emirates" value={countryForm.country_name} onChange={e => setCountryForm({ ...countryForm, country_name: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Phone Code</label>
                      <input type="text" placeholder="+971" value={countryForm.phone_code} onChange={e => setCountryForm({ ...countryForm, phone_code: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Currency</label>
                      <input type="text" placeholder="AED" value={countryForm.currency_code} onChange={e => setCountryForm({ ...countryForm, currency_code: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ background: '#f1f5f9', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ background: '#0284c7', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '6px', fontSize: '0.85rem', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Country'}</button>
                </div>
              </form>
            )}

            {/* REGION FORM */}
            {modalType === 'region' && (
              <form onSubmit={handleSaveRegion}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Country *</label>
                    <select required value={regionForm.country_id} onChange={e => setRegionForm({ ...regionForm, country_id: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}>
                      <option value="">-- Choose Country --</option>
                      {countries.map(c => <option key={c.id} value={c.id}>{c.country_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Region / State / Emirate Name *</label>
                    <input type="text" required placeholder="e.g. Dubai or Hawalli or California" value={regionForm.region_name} onChange={e => setRegionForm({ ...regionForm, region_name: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Classification Type</label>
                    <select value={regionForm.region_type} onChange={e => setRegionForm({ ...regionForm, region_type: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}>
                      <option value="emirate">Emirate (e.g. UAE)</option>
                      <option value="governorate">Governorate (e.g. Kuwait/Egypt)</option>
                      <option value="province">Province (e.g. Saudi/Canada)</option>
                      <option value="state">State (e.g. India/US)</option>
                      <option value="county">County (e.g. UK)</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ background: '#f1f5f9', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ background: '#0284c7', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '6px', fontSize: '0.85rem', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Region'}</button>
                </div>
              </form>
            )}

            {/* CITY FORM */}
            {modalType === 'city' && (
              <form onSubmit={handleSaveCity}>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>State / Emirate / Province *</label>
                    <select required value={cityForm.region_id} onChange={e => setCityForm({ ...cityForm, region_id: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }}>
                      <option value="">-- Choose State / Emirate --</option>
                      {regions.map(r => <option key={r.id} value={r.id}>{r.region_name} ({r.country_name})</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>City / Service Area Name *</label>
                    <input type="text" required placeholder="e.g. Downtown Dubai or Salmiya" value={cityForm.city_name} onChange={e => setCityForm({ ...cityForm, city_name: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.3rem' }}>Postal Code / Zip (Optional)</label>
                    <input type="text" placeholder="e.g. 00000" value={cityForm.postal_code} onChange={e => setCityForm({ ...cityForm, postal_code: e.target.value })} style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem' }} />
                  </div>
                </div>
                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ background: '#f1f5f9', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={saving} style={{ background: '#0284c7', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '6px', fontSize: '0.85rem', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save City/Area'}</button>
                </div>
              </form>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
